// Phase G — Conversion-trigger pump.
//
// Tiny hook that fires `record_conversion_trigger(p_kind, p_meta)` once
// per (kind × day × user). The RPC itself is idempotent server-side, so
// even if the hook re-fires from a re-render or a navigation back to
// the page we won't duplicate rows. We add a session-local in-memory
// guard to avoid the RPC round-trip on second render of the same page.
//
// Anon visitors: the RPC short-circuits and returns null when
// auth.uid() is null. We still gate the call on `session?.user?.id`
// here to skip the network hop entirely.
//
// Doctrine: never fabricate. The hook accepts a kind + meta and writes
// what it's given — it never invents trigger payloads from prior state.

import { useEffect, useMemo, useRef } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

const firedThisSession = new Set<string>();

export function useConversionTriggerPump(
  triggerKind: string | null,
  triggerMeta: Record<string, unknown> = {},
  enabled: boolean = true,
): void {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const metaRef = useRef(triggerMeta);
  metaRef.current = triggerMeta;

  useEffect(() => {
    if (!enabled) return;
    if (!triggerKind) return;
    if (!userId) return;

    const key = `${userId}::${triggerKind}::${new Date()
      .toISOString()
      .slice(0, 10)}`;
    if (firedThisSession.has(key)) return;
    firedThisSession.add(key);

    // db/types.ts may not include the new RPC name; cast.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    void sb
      .rpc("record_conversion_trigger", {
        p_kind: triggerKind,
        p_meta: metaRef.current ?? {},
      })
      .then(({ error }: { error: { message: string } | null }) => {
        if (error) {
          // Silent on production; trigger pump must never break a page.
          console.warn("[strikza] record_conversion_trigger failed", error.message);
          firedThisSession.delete(key); // allow retry next mount
        }
      });
  }, [enabled, triggerKind, userId, supabase]);
}
